/**
 * IndexedDB Database Schema using Dexie
 * Handles offline data storage for Restaurant ERP
 */

import Dexie from 'dexie';

class OfflineDatabase extends Dexie {
  constructor() {
    super('RestaurantERP_Offline');
    
    // Define database schema
    // Version 4: Added shiftEndQueue for offline shift end support
    this.version(4).stores({
      // Transaction queue for pending API requests
      transactionQueue: '++id, transactionId, endpoint, method, priority, status, createdAt, retryCount, branchId',
      
      // Cached orders (recent orders for offline viewing) - FIXED: use _id as primary key
      ordersCache: '_id, orderId, branchId, createdAt, updatedAt, syncStatus, offlineOrder',
      
      // Cached products/menu items
      productsCache: 'productId, branchId, categoryId, updatedAt',
      
      // Cached customers
      customersCache: 'customerId, branchId, updatedAt',
      
      // Sync metadata (track last sync times)
      syncMetadata: 'key, lastSyncTime, entityType, branchId',
      
      // Conflict log (failed syncs requiring manual resolution)
      conflictLog: '++id, transactionId, conflictType, data, createdAt, resolved',
      
      // API response cache (for GET requests)
      apiCache: 'url, data, timestamp',
      
      // Image cache (for product images)
      imageCache: 'url, blob, timestamp',
      
      // Offline settings
      settings: 'key, value',
      
      // Shift end queue (for offline shift end support)
      shiftEndQueue: 'shiftId, branchId, timestamp, status'
    });

    // Version 5: Token Display column background media (local-only)
    this.version(5).stores({
      // Transaction queue for pending API requests
      transactionQueue: '++id, transactionId, endpoint, method, priority, status, createdAt, retryCount, branchId',
      
      // Cached orders (recent orders for offline viewing) - FIXED: use _id as primary key
      ordersCache: '_id, orderId, branchId, createdAt, updatedAt, syncStatus, offlineOrder',
      
      // Cached products/menu items
      productsCache: 'productId, branchId, categoryId, updatedAt',
      
      // Cached customers
      customersCache: 'customerId, branchId, updatedAt',
      
      // Sync metadata (track last sync times)
      syncMetadata: 'key, lastSyncTime, entityType, branchId',
      
      // Conflict log (failed syncs requiring manual resolution)
      conflictLog: '++id, transactionId, conflictType, data, createdAt, resolved',
      
      // API response cache (for GET requests)
      apiCache: 'url, data, timestamp',
      
      // Image cache (for product images)
      imageCache: 'url, blob, timestamp',
      
      // Offline settings
      settings: 'key, value',
      
      // Shift end queue (for offline shift end support)
      shiftEndQueue: 'shiftId, branchId, timestamp, status',

      // Token Display - background media blobs for Preparing/Ready columns
      tokenColumnBgMedia: '++id, scope, name, type, size, createdAt'
    });

    // Define table references
    this.transactionQueue = this.table('transactionQueue');
    this.ordersCache = this.table('ordersCache');
    this.productsCache = this.table('productsCache');
    this.customersCache = this.table('customersCache');
    this.syncMetadata = this.table('syncMetadata');
    this.conflictLog = this.table('conflictLog');
    this.apiCache = this.table('apiCache');
    this.imageCache = this.table('imageCache');
    this.settings = this.table('settings');
    this.shiftEndQueue = this.table('shiftEndQueue');
    this.tokenColumnBgMedia = this.table('tokenColumnBgMedia');
  }

  // ==================== SHIFT END QUEUE METHODS ====================

  /**
   * Queue a shift end request for offline processing
   */
  async queueShiftEnd(body) {
    try {
      const shiftId = body.shiftId || body._id;
      if (!shiftId) {
        console.error('❌ Cannot queue shift end: no shiftId provided');
        return false;
      }

      // Check if already queued
      const existing = await this.shiftEndQueue.get(shiftId);
      if (existing) {
        console.log(`⚠️ Shift end already queued: ${shiftId}`);
        return existing;
      }

      const queueEntry = {
        shiftId,
        branchId: body.branchId,
        timestamp: Date.now(),
        status: 'pending',
        requestBody: body,
        endDate: body.endDate,
        empId: body.empId,
        posDeviceId: body.posDeviceId,
        posDeviceName: body.posDeviceName
      };

      await this.shiftEndQueue.put(queueEntry);
      console.log(`📥 Shift end queued: ${shiftId}`);
      return queueEntry;
    } catch (error) {
      console.error('❌ Error queuing shift end:', error);
      throw error;
    }
  }

  /**
   * Get all pending shift end requests
   */
  async getPendingShiftEnds() {
    try {
      return await this.shiftEndQueue
        .where('status')
        .equals('pending')
        .toArray();
    } catch (error) {
      console.error('❌ Error getting pending shift ends:', error);
      return [];
    }
  }

  /**
   * Update shift end queue status
   */
  async updateShiftEndStatus(shiftId, status, error = null) {
    try {
      await this.shiftEndQueue
        .where('shiftId')
        .equals(shiftId)
        .modify({
          status,
          error: error ? error.message : null,
          updatedAt: Date.now()
        });
      console.log(`📝 Shift end status updated: ${shiftId} → ${status}`);
    } catch (error) {
      console.error('❌ Error updating shift end status:', error);
    }
  }

  /**
   * Remove shift end from queue (after successful sync)
   */
  async removeShiftEnd(shiftId) {
    try {
      await this.shiftEndQueue.delete(shiftId);
      console.log(`🗑️ Shift end removed from queue: ${shiftId}`);
    } catch (error) {
      console.error('❌ Error removing shift end:', error);
    }
  }

  /**
   * Check if there's a pending shift end for a branch
   */
  async hasPendingShiftEnd(branchId) {
    try {
      const pending = await this.shiftEndQueue
        .where('branchId')
        .equals(branchId)
        .and(item => item.status === 'pending')
        .first();
      return !!pending;
    } catch (error) {
      console.error('❌ Error checking pending shift end:', error);
      return false;
    }
  }

  /**
   * Get pending shift end for a branch
   */
  async getPendingShiftEndForBranch(branchId) {
    try {
      return await this.shiftEndQueue
        .where('branchId')
        .equals(branchId)
        .and(item => item.status === 'pending')
        .first();
    } catch (error) {
      console.error('❌ Error getting pending shift end for branch:', error);
      return null;
    }
  }

  /**
   * Clear stale transactions that have exceeded max retries
   */
  async clearStaleTransactions(maxRetries = 3) {
    try {
      const staleTransactions = await this.transactionQueue
        .filter(txn => txn.retryCount >= maxRetries)
        .toArray();
      
      if (staleTransactions.length > 0) {
        console.log(`🗑️ Clearing ${staleTransactions.length} stale transactions`);
        for (const txn of staleTransactions) {
          await this.transactionQueue.delete(txn.id);
        }
      }
      
      return staleTransactions.length;
    } catch (error) {
      console.error('❌ Error clearing stale transactions:', error);
      return 0;
    }
  }

  /**
   * Store local shift status (for UI display when offline)
   */
  async setLocalShiftStatus(branchId, status, shiftData = null) {
    try {
      const key = `localShiftStatus_${branchId}`;
      await this.settings.put({
        key,
        value: {
          status,
          shiftData,
          timestamp: Date.now()
        }
      });
      console.log(`💾 Local shift status saved: ${branchId} → ${status}`);
    } catch (error) {
      console.error('❌ Error setting local shift status:', error);
    }
  }

  /**
   * Get local shift status
   */
  async getLocalShiftStatus(branchId) {
    try {
      const key = `localShiftStatus_${branchId}`;
      const result = await this.settings.get(key);
      return result?.value || null;
    } catch (error) {
      console.error('❌ Error getting local shift status:', error);
      return null;
    }
  }

  /**
   * Clear local shift status (after successful sync)
   */
  async clearLocalShiftStatus(branchId) {
    try {
      const key = `localShiftStatus_${branchId}`;
      await this.settings.delete(key);
      console.log(`🗑️ Local shift status cleared: ${branchId}`);
    } catch (error) {
      console.error('❌ Error clearing local shift status:', error);
    }
  }

  // ==================== CHECKOUT REPORT CALCULATION ====================

  /**
   * Calculate checkout payment report from locally cached orders
   * This aggregates payment data for offline checkout display
   */
  async calculateOfflineCheckoutReport(shiftId) {
    try {
      console.log(`📊 Calculating offline checkout report for shift: ${shiftId}`);
      
      // Get all orders from cache
      const allOrders = await this.ordersCache.toArray();
      
      // Filter orders for this shift that are completed/paid
      // Also include orders from today if shiftId doesn't match (cached orders might not have shiftId)
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      
      const shiftOrders = allOrders.filter(order => {
        const orderShiftId = order.shiftId || order.shiftPk;
        const isPaid = order.isPaid === true || order.paymentStatus === 'PAID' || order.ordStatus === 'COMPLETED';
        
        // Match by shiftId if available
        if (orderShiftId === shiftId && isPaid) {
          return true;
        }
        
        // Fallback: include today's paid orders if no shiftId match
        // This helps when cached orders don't have shiftId stored
        if (!orderShiftId && isPaid) {
          const orderDate = order.orderDate || order.createdAt || '';
          const orderDateOnly = orderDate.split(' ')[0].split('T')[0];
          // Handle both YYYY-MM-DD and DD/MM/YYYY formats
          const normalizedOrderDate = orderDateOnly.includes('/') 
            ? orderDateOnly.split('/').reverse().join('-') 
            : orderDateOnly;
          return normalizedOrderDate === today;
        }
        
        return false;
      });
      
      console.log(`📊 Found ${shiftOrders.length} paid orders for shift`);
      
      // Aggregate by payment type (journal)
      const paymentSummary = {};
      const orderTypeSummary = {};
      let totalAmount = 0;
      
      for (const order of shiftOrders) {
        // Aggregate by order type
        const ordType = order.ordType || 'DINE_IN';
        if (!orderTypeSummary[ordType]) {
          orderTypeSummary[ordType] = { ordType, amtTotal: 0, count: 0 };
        }
        orderTypeSummary[ordType].amtTotal += Number(order.amtTotal) || 0;
        orderTypeSummary[ordType].count += 1;
        
        // Aggregate by payment method
        const paymentDetails = order.paymentDetails || order.paymentMethod || [];
        const payments = Array.isArray(paymentDetails) ? paymentDetails : [paymentDetails];
        
        for (const payment of payments) {
          if (!payment) continue;
          
          const journalName = payment.journalName || payment.type || 'Cash';
          const journalId = payment.journalId || payment.paymentJournelId || '';
          const amount = Number(payment.amount) || Number(order.amtTotal) || 0;
          
          if (!paymentSummary[journalName]) {
            paymentSummary[journalName] = {
              name: journalName,
              type: payment.type || journalName.toLowerCase(),
              journalId: journalId,
              totalAmount: 0,
              totalCommission: 0,
              count: 0
            };
          }
          
          paymentSummary[journalName].totalAmount += amount;
          paymentSummary[journalName].totalCommission += Number(payment.commission) || 0;
          paymentSummary[journalName].count += 1;
          totalAmount += amount;
        }
        
        // If no payment details, assume cash
        if (payments.length === 0 || !payments[0]) {
          const amount = Number(order.amtTotal) || 0;
          if (!paymentSummary['Cash']) {
            paymentSummary['Cash'] = {
              name: 'Cash',
              type: 'cash',
              journalId: '',
              totalAmount: 0,
              totalCommission: 0,
              count: 0
            };
          }
          paymentSummary['Cash'].totalAmount += amount;
          paymentSummary['Cash'].count += 1;
          totalAmount += amount;
        }
      }
      
      // Convert to arrays
      const cashCardSummaryData = Object.values(paymentSummary);
      const posOrderSummaryData = Object.values(orderTypeSummary);
      
      console.log(`📊 Checkout report calculated: ${cashCardSummaryData.length} payment types, total: ${totalAmount}`);
      
      return {
        success: true,
        offline: true,
        message: 'Checkout report calculated from local data',
        data: {
          cashCardSummary: {
            data: cashCardSummaryData,
            amountTotal: totalAmount
          },
          posOrderSummary: posOrderSummaryData
        }
      };
    } catch (error) {
      console.error('❌ Error calculating offline checkout report:', error);
      return {
        success: true,
        offline: true,
        message: 'Could not calculate checkout report',
        data: {
          cashCardSummary: { data: [], amountTotal: 0 },
          posOrderSummary: []
        }
      };
    }
  }

  /**
   * Add transaction to queue
   */
  async addTransaction(transaction) {
    try {
      const id = await this.transactionQueue.add({
        ...transaction,
        createdAt: new Date().toISOString(),
        retryCount: 0,
        status: 'pending'
      });
      return id;
    } catch (error) {
      console.error('Error adding transaction to queue:', error);
      throw error;
    }
  }

  /**
   * Get all pending transactions
   */
  async getPendingTransactions(branchId = null) {
    try {
      let query = this.transactionQueue
        .where('status')
        .equals('pending')
        .or('status')
        .equals('retrying');

      if (branchId) {
        query = query.and(item => item.branchId === branchId);
      }

      return await query.sortBy('priority');
    } catch (error) {
      console.error('Error getting pending transactions:', error);
      return [];
    }
  }

  /**
   * Get the next KOT token number for offline orders
   * Continues from last online KOT token if available
   */
  async getNextKotToken(branchId, shiftId) {
    try {
      // Get the current KOT counter from settings
      const key = `kotCounter_${branchId}_${shiftId || 'current'}`;
      let counter = await this.settings.get(key);
      // Fallback: if shift-specific key not found, check 'current'
      if ((!counter || counter.value === undefined) && shiftId) {
        const altKey = `kotCounter_${branchId}_current`;
        const altCounter = await this.settings.get(altKey);
        if (altCounter && altCounter.value !== undefined) {
          counter = altCounter;
        }
      }
      
      if (counter && counter.value !== undefined) {
        // Increment and save
        const nextToken = counter.value + 1;
        await this.settings.put({ key, value: nextToken });
        console.log(`🎫 KOT counter incremented: ${counter.value} → ${nextToken}`);
        return nextToken;
      } else {
        // Initialize counter from last online KOT token
        console.log(`🔍 Initializing KOT counter for ${key}...`);
        
        // Get the last cached KOT token from settings (set when online order is created)
        const lastOnlineKey = `lastKotToken_${branchId}_${shiftId || 'current'}`;
        let lastOnlineToken = await this.settings.get(lastOnlineKey);
        // Fallback: try opposite key as well
        if ((!lastOnlineToken || lastOnlineToken.value === undefined) && shiftId) {
          const altLastKey = `lastKotToken_${branchId}_current`;
          lastOnlineToken = await this.settings.get(altLastKey);
        }
        
        let initialToken = 1;
        if (lastOnlineToken && lastOnlineToken.value) {
          // Continue from last online token
          initialToken = lastOnlineToken.value + 1;
          console.log(`📊 Found last online KOT token: ${lastOnlineToken.value}, starting offline at: ${initialToken}`);
        } else {
          console.log(`📊 No online KOT token found, starting at: 1`);
        }
        
        await this.settings.put({ key, value: initialToken });
        return initialToken;
      }
    } catch (error) {
      console.error('❌ Error getting next KOT token:', error);
      // Fallback to 1 if error
      return 1;
    }
  }

  /**
   * Update the last online KOT token (called when online order is created)
   */
  async updateLastOnlineKotToken(branchId, shiftId, kotToken) {
    try {
      // Save to shift-specific key (if provided) and 'current' for robust fallback
      const keySpecific = `lastKotToken_${branchId}_${shiftId || 'current'}`;
      await this.settings.put({ key: keySpecific, value: kotToken });
      const keyCurrent = `lastKotToken_${branchId}_current`;
      await this.settings.put({ key: keyCurrent, value: kotToken });
      console.log(`💾 Saved last online KOT token: ${kotToken}`);
    } catch (error) {
      console.error('❌ Error saving last online KOT token:', error);
    }
  }

  /**
   * Reset KOT token counter (called when shift changes)
   */
  async resetKotCounter(branchId, shiftId) {
    try {
      const key = `kotCounter_${branchId}_${shiftId || 'current'}`;
      await this.settings.put({ key, value: 0 });
      console.log(`🔄 Reset KOT counter for shift: ${shiftId}`);
    } catch (error) {
      console.error('Error resetting KOT counter:', error);
    }
  }

  /**
   * Get count of pending transactions
   */
  async getPendingCount(branchId = null) {
    try {
      const pending = await this.getPendingTransactions(branchId);
      return pending.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }

  /**
   * Update transaction status
   */
  async updateTransactionStatus(transactionId, status, error = null) {
    try {
      await this.transactionQueue
        .where('transactionId')
        .equals(transactionId)
        .modify({
          status,
          error: error ? error.message : null,
          updatedAt: new Date().toISOString()
        });
    } catch (error) {
      console.error('Error updating transaction status:', error);
    }
  }

  /**
   * Increment retry count
   */
  async incrementRetryCount(transactionId) {
    try {
      await this.transactionQueue
        .where('transactionId')
        .equals(transactionId)
        .modify(transaction => {
          transaction.retryCount = (transaction.retryCount || 0) + 1;
          transaction.status = 'retrying';
        });
    } catch (error) {
      console.error('Error incrementing retry count:', error);
    }
  }

  /**
   * Delete transaction from queue
   */
  async deleteTransaction(transactionId) {
    try {
      await this.transactionQueue
        .where('transactionId')
        .equals(transactionId)
        .delete();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  }

  /**
   * Clear all pending transactions (for testing/reset)
   */
  async clearAllPendingTransactions() {
    try {
      await this.transactionQueue.clear();
      console.log('🗑️ Cleared all pending transactions');
    } catch (error) {
      console.error('Error clearing transactions:', error);
    }
  }

  /**
   * Clear stale auth transactions that should never be queued
   * Call this on app startup to clean up any accidentally queued auth requests
   */
  async clearStaleAuthTransactions() {
    try {
      const authEndpoints = [
        'admin/login',
        'admin/checkdevice',
        'admin/logout',
        'admin/refreshToken'
      ];
      
      const allTxns = await this.transactionQueue.toArray();
      let clearedCount = 0;
      
      for (const txn of allTxns) {
        const isAuth = authEndpoints.some(ep => txn.endpoint?.includes(ep));
        if (isAuth) {
          await this.transactionQueue.delete(txn.id);
          clearedCount++;
        }
      }
      
      if (clearedCount > 0) {
        console.log(`🗑️ Cleared ${clearedCount} stale auth transactions from queue`);
      }
      return clearedCount;
    } catch (error) {
      console.error('Error clearing stale auth transactions:', error);
      return 0;
    }
  }

  /**
   * Clear invalid transactions (404 endpoints, etc.)
   */
  async clearInvalidTransactions() {
    try {
      const invalidEndpoints = [
        'listCompletedSelfOrder',
        'viewReservationList'
      ];
      
      const allTxns = await this.transactionQueue.toArray();
      let clearedCount = 0;
      
      for (const txn of allTxns) {
        const isInvalid = invalidEndpoints.some(ep => txn.endpoint?.includes(ep));
        if (isInvalid) {
          await this.transactionQueue.delete(txn.id);
          clearedCount++;
          console.log(`🗑️ Cleared invalid transaction: ${txn.endpoint}`);
        }
      }
      
      console.log(`🗑️ Cleared ${clearedCount} invalid transactions`);
      return clearedCount;
    } catch (error) {
      console.error('Error clearing invalid transactions:', error);
      return 0;
    }
  }

  /**
   * Debug: View all pending transactions with details
   */
  async debugPendingTransactions() {
    try {
      const allTxns = await this.transactionQueue.toArray();
      console.log(`\n📋 DEBUG: ${allTxns.length} pending transactions:`);
      
      for (const txn of allTxns) {
        const data = typeof txn.data === 'string' ? JSON.parse(txn.data) : txn.data;
        console.log(`  - ${txn.method} ${txn.endpoint}`);
        console.log(`    ID: ${txn.transactionId}`);
        console.log(`    dependsOn: ${txn.dependsOn || 'none'}`);
        console.log(`    orderId in data: ${data?.orderId || data?.id || 'none'}`);
      }
      
      return allTxns;
    } catch (error) {
      console.error('Error debugging transactions:', error);
      return [];
    }
  }

  /**
   * Cache order data
   * NOTE: This is for caching ONLINE orders. Use storeOfflineOrder for offline orders.
   */
  async cacheOrder(order) {
    try {
      // Ensure _id exists
      if (!order._id && !order.orderId) {
        console.warn('⚠️ Cannot cache order without _id or orderId');
        return;
      }
      
      const orderId = order._id || order.orderId;
      
      // Preserve offlineOrder flag if already set, otherwise default to false (online order)
      const isOfflineOrder = order.offlineOrder === true;
      
      await this.ordersCache.put({
        ...order,
        _id: orderId,  // Primary key
        orderId: orderId,  // For compatibility
        updatedAt: new Date().toISOString(),
        syncStatus: isOfflineOrder ? 'pending' : 'synced',
        offlineOrder: isOfflineOrder  // Preserve existing flag
      });
    } catch (error) {
      console.error('Error caching order:', error);
    }
  }

  /**
   * Get cached orders
   */
  async getCachedOrders(branchId, limit = 100) {
    try {
      return await this.ordersCache
        .where('branchId')
        .equals(branchId)
        .reverse()
        .sortBy('createdAt')
        .then(orders => orders.slice(0, limit));
    } catch (error) {
      console.error('Error getting cached orders:', error);
      return [];
    }
  }

  /**
   * Cache products/menu items
   */
  async cacheProducts(products, branchId) {
    try {
      const productsWithMeta = products.map(product => ({
        ...product,
        branchId,
        updatedAt: new Date().toISOString()
      }));
      await this.productsCache.bulkPut(productsWithMeta);
    } catch (error) {
      console.error('Error caching products:', error);
    }
  }

  /**
   * Get cached products
   */
  async getCachedProducts(branchId) {
    try {
      return await this.productsCache
        .where('branchId')
        .equals(branchId)
        .toArray();
    } catch (error) {
      console.error('Error getting cached products:', error);
      return [];
    }
  }

  /**
   * Update sync metadata
   */
  async updateSyncMetadata(entityType, branchId) {
    try {
      const key = `${entityType}_${branchId}`;
      await this.syncMetadata.put({
        key,
        entityType,
        branchId,
        lastSyncTime: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating sync metadata:', error);
    }
  }

  /**
   * Get last sync time
   */
  async getLastSyncTime(entityType, branchId) {
    try {
      const key = `${entityType}_${branchId}`;
      const metadata = await this.syncMetadata.get(key);
      return metadata ? metadata.lastSyncTime : null;
    } catch (error) {
      console.error('Error getting last sync time:', error);
      return null;
    }
  }

  /**
   * Log conflict
   */
  async logConflict(conflict) {
    try {
      await this.conflictLog.add({
        ...conflict,
        createdAt: new Date().toISOString(),
        resolved: false
      });
    } catch (error) {
      console.error('Error logging conflict:', error);
    }
  }

  /**
   * Get unresolved conflicts
   */
  async getUnresolvedConflicts() {
    try {
      const allConflicts = await this.conflictLog.toArray();
      return allConflicts.filter(conflict => conflict.resolved === false);
    } catch (error) {
      console.error('Error getting unresolved conflicts:', error);
      return [];
    }
  }

  /**
   * Clear all conflicts (resolve them)
   */
  async clearAllConflicts() {
    try {
      await this.conflictLog.clear();
      console.log('🗑️ Cleared all conflict logs');
    } catch (error) {
      console.error('Error clearing conflicts:', error);
    }
  }

  /**
   * Mark conflict as resolved
   */
  async resolveConflict(conflictId) {
    try {
      await this.conflictLog
        .where('id')
        .equals(conflictId)
        .modify({ resolved: true });
      console.log(`✅ Resolved conflict: ${conflictId}`);
    } catch (error) {
      console.error('Error resolving conflict:', error);
    }
  }

  /**
   * Store offline order
   */
  async storeOfflineOrder(order) {
    try {
      // Ensure _id exists (use orderId if _id is missing)
      const orderId = order._id || order.orderId;
      
      // Mark as offline order
      const offlineOrder = {
        ...order,
        offlineOrder: true,
        syncStatus: 'pending'
      };
      
      await this.ordersCache.put(offlineOrder);
      console.log(`💾 Stored offline order: ${order._id} with ${order.orderInfo?.length || 0} items`);
      console.log(`   Status: ${order.ordStatus}, isPaid: ${order.isPaid}, paymentStatus: ${order.paymentStatus}`);
      return offlineOrder;
    } catch (error) {
      console.error('Error storing offline order:', error);
      throw error;
    }
  }

  /**
   * Delete offline order (e.g., after successful sync)
   */
  async deleteOfflineOrder(orderId) {
    try {
      await this.ordersCache.delete(orderId);
      console.log(`🗑️ Deleted offline order from IndexedDB: ${orderId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting offline order ${orderId}:`, error);
      return false;
    }
  }

  /**
   * Update offline order (e.g., for payment)
   */
  async updateOfflineOrder(orderId, updates) {
    try {
      const existing = await this.ordersCache.get(orderId);
      if (!existing) {
        console.warn(`⚠️ Order ${orderId} not found in cache`);
        return null;
      }
      
      const updated = {
        ...existing,
        ...updates,
        _id: orderId,  // Ensure primary key doesn't change
        offlineOrder: true,  // ALWAYS preserve offline flag
        updatedAt: new Date().toISOString()
      };
      
      await this.ordersCache.put(updated);
      console.log(`✅ Updated offline order: ${orderId}`, updates);
      console.log(`   Order has ${updated.orderInfo?.length || 0} items after update`);
      console.log(`   offlineOrder flag: ${updated.offlineOrder}, ordStatus: ${updated.ordStatus}`);
      return updated;
    } catch (error) {
      console.error('Error updating offline order:', error);
      throw error;
    }
  }
  
  /**
   * Get single offline order by ID
   */
  async getOfflineOrderById(orderId) {
    try {
      const order = await this.ordersCache.get(orderId);
      if (order && order.offlineOrder) {
        console.log(`📦 Retrieved offline order from cache: ${orderId}`);
        return order;
      }
      return null;
    } catch (error) {
      console.error('Error getting offline order by ID:', error);
      return null;
    }
  }

  /**
   * Get all offline orders (includes BOTH offline orders AND synced cached orders)
   */
  async getOfflineOrders() {
    try {
      const allOrders = await this.ordersCache.toArray();
      
      // Return ALL orders - both offline (offlineOrder: true) and synced cached orders (offlineOrder: false)
      // This ensures that when you go offline after syncing, you still see the synced orders
      console.log(`📦 Found ${allOrders.length} total orders in IndexedDB`);
      
      // Filter out orders with missing CRITICAL fields (be lenient - some fields can be missing)
      const validOrders = allOrders.filter(order => {
        const hasRequiredFields = order._id !== undefined && 
                                 order.orderInfo !== undefined;
        if (!hasRequiredFields) {
          console.warn(`⚠️ Skipping invalid order: ${order._id || 'unknown'} - missing _id or orderInfo`);
        }
        return hasRequiredFields;
      });
      
      const offlineCount = validOrders.filter(o => o.offlineOrder === true).length;
      const cachedCount = validOrders.filter(o => o.offlineOrder === false).length;
      console.log(`✅ Returning ${validOrders.length} orders (${offlineCount} offline, ${cachedCount} cached)`);
      
      return validOrders;
    } catch (error) {
      console.error('Error getting offline orders:', error);
      return [];
    }
  }

  /**
   * Cache API response
   */
  async cacheApiResponse(url, data) {
    try {
      await this.apiCache.put({
        url: url,
        data: data,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error caching API response:', error);
      throw error;
    }
  }

  /**
   * Get cached data by URL
   */
  async getCachedData(url) {
    try {
      // Try to find cached response for this URL
      const cached = await this.apiCache.get(url);
      return cached;
    } catch (error) {
      console.error('Error getting cached data:', error);
      return null;
    }
  }

  /**
   * Clear authentication-related cache (for security)
   * Should be called on login page load
   */
  async clearAuthCache() {
    try {
      const authPatterns = ['checkdevice', 'login', 'trusteddevices', 'requestdeviceotp', 'verifydeviceotp', 'searchuser'];
      const allCached = await this.apiCache.toArray();
      
      for (const cached of allCached) {
        if (authPatterns.some(pattern => cached.url?.toLowerCase().includes(pattern))) {
          await this.apiCache.delete(cached.url);
          console.log(`🔒 Cleared auth cache: ${cached.url}`);
        }
      }
    } catch (error) {
      console.error('Error clearing auth cache:', error);
    }
  }

  /**
   * Clear ALL user-specific cached data (for logout / user switch)
   * Prevents cross-user data contamination when switching accounts
   */
  async clearAllUserCache() {
    try {
      await this.apiCache.clear();
      await this.ordersCache.clear();
      await this.productsCache.clear();
      await this.customersCache.clear();
      await this.syncMetadata.clear();
      console.log('🧹 Cleared all user-specific cache (apiCache, ordersCache, productsCache, customersCache, syncMetadata)');
    } catch (error) {
      console.error('Error clearing all user cache:', error);
    }
  }

  /**
   * Clear old cached data (cleanup)
   */
  async clearOldCache(daysToKeep = 7) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      const cutoffISO = cutoffDate.toISOString();

      // Clear old orders
      await this.ordersCache
        .where('updatedAt')
        .below(cutoffISO)
        .delete();

      // Clear old products
      await this.productsCache
        .where('updatedAt')
        .below(cutoffISO)
        .delete();

      console.log(`Cleared cache older than ${daysToKeep} days`);
    } catch (error) {
      console.error('Error clearing old cache:', error);
    }
  }

  /**
   * Get database statistics
   */
  async getStats() {
    try {
      const [
        pendingCount,
        ordersCount,
        productsCount,
        conflictsCount
      ] = await Promise.all([
        this.transactionQueue.where('status').equals('pending').count(),
        this.ordersCache.count(),
        this.productsCache.count(),
        this.conflictLog.where('resolved').equals(false).count()
      ]);

      return {
        pendingTransactions: pendingCount,
        cachedOrders: ordersCount,
        cachedProducts: productsCount,
        unresolvedConflicts: conflictsCount
      };
    } catch (error) {
      console.error('Error getting database stats:', error);
      return null;
    }
  }

  /**
   * Cache image as blob
   */
  async cacheImage(url, blob) {
    try {
      await this.imageCache.put({
        url,
        blob,
        timestamp: Date.now()
      });
      console.log(`🖼️ Cached image: ${url}`);
    } catch (error) {
      console.error('Error caching image:', error);
    }
  }

  /**
   * Get cached image
   */
  async getCachedImage(url) {
    try {
      const cached = await this.imageCache.get(url);
      if (cached) {
        console.log(`✅ Retrieved cached image: ${url}`);
        return cached.blob;
      }
      return null;
    } catch (error) {
      console.error('Error getting cached image:', error);
      return null;
    }
  }

  /**
   * Clear old cached images (older than 30 days)
   */
  async clearOldImages() {
    try {
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const oldImages = await this.imageCache
        .where('timestamp')
        .below(thirtyDaysAgo)
        .toArray();
      
      if (oldImages.length > 0) {
        await this.imageCache
          .where('timestamp')
          .below(thirtyDaysAgo)
          .delete();
        console.log(`🗑️ Cleared ${oldImages.length} old cached images`);
      }
    } catch (error) {
      console.error('Error clearing old images:', error);
    }
  }

  /**
   * Clear all data (for testing/reset)
   */
  async clearAllData() {
    try {
      await Promise.all([
        this.transactionQueue.clear(),
        this.ordersCache.clear(),
        this.productsCache.clear(),
        this.customersCache.clear(),
        this.syncMetadata.clear(),
        this.conflictLog.clear(),
        this.imageCache.clear()
      ]);
      console.log('All offline data cleared');
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }

  /**
   * Get KOT orders (offline + cached) with filter
   */
  async getKotOrders(branchId, statusFilter = null) {
    try {
      // Get all orders (offline + cached) for the branch
      const allOrders = await this.ordersCache
        .where('branchId')
        .equals(branchId)
        .reverse()
        .sortBy('createdAt');
      
      // Filter by status if provided
      let filteredOrders = allOrders;
      if (statusFilter && statusFilter !== 'ALL') {
        filteredOrders = allOrders.filter(order => {
          // Map order status to KOT status
          if (statusFilter === 'PENDING') {
            return order.ordStatus === 'PENDING' || order.kotStatus === 'PENDING';
          } else if (statusFilter === 'COOKING') {
            return order.kotStatus === 'COOKING';
          } else if (statusFilter === 'COMPLETED') {
            return order.kotStatus === 'COMPLETED';
          }
          return true;
        });
      }
      
      console.log(`📋 KOT: Found ${filteredOrders.length} orders (filter: ${statusFilter || 'ALL'})`);
      return filteredOrders;
    } catch (error) {
      console.error('Error getting KOT orders:', error);
      return [];
    }
  }

  /**
   * Update KOT status for an order
   */
  async updateKotStatus(orderId, kotStatus) {
    try {
      await this.ordersCache
        .where('_id')
        .equals(orderId)
        .modify({ kotStatus, updatedAt: new Date().toISOString() });
      
      console.log(`✅ Updated KOT status for order ${orderId} to ${kotStatus}`);
      return true;
    } catch (error) {
      console.error('Error updating KOT status:', error);
      return false;
    }
  }

  /**
   * Clear all data (for testing/debugging)
   */
  async clearAll() {
    try {
      await this.ordersCache.clear();
      await this.productsCache.clear();
      await this.transactionQueue.clear();
      await this.settings.clear();
      console.log('🗑️ Cleared all offline data');
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }
}

// Export singleton instance
const db = new OfflineDatabase();

export default db;
